import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Button } from "../../Components/ui/button";
import { Label } from "../../Components/ui/label";
import { Textarea } from "../../Components/ui/textarea";
import {
  useGetReportByIdQuery,
  useDeleteReportMutation,
  useLazyGetReportPDFQuery,
  useUpdateReportMutation,
} from "../../../redux/reducers/apiSlice";
import FullPageNexusLogoAnimation from "../../Reusable/FullPageNexusLogoAnimation/FullPageNexusLogoAnimation";
import jsPDF from "jspdf";
import * as Sentry from "@sentry/react";

/** Returns a clean, human-readable metric label (without forcing "Total" prefix) */
function getMetricTotalLabel(name: string): string {
  if (!name || typeof name !== "string") return "Metric";
  return name.trim();
}

const ViewReport = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: report, isLoading, error } = useGetReportByIdQuery(
    { id: id! },
    { skip: !id }
  );
  const [deleteReport, { isLoading: isDeleting }] = useDeleteReportMutation();
  const [getReportPDF] = useLazyGetReportPDFQuery();
  const [updateReport, { isLoading: isUpdating }] = useUpdateReportMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedNarrative, setEditedNarrative] = useState("");

  useEffect(() => {
    setEditedNarrative(report?.narrative ?? "");
  }, [report]);

  const handleSaveEdit = async () => {
    if (!id || !report) return;

    try {
      await updateReport({
        id,
        data: {
          grantName: report.grantName,
          startDate: report.startDate,
          endDate: report.endDate,
          metrics: report.metrics || [],
          narrative: editedNarrative,
          organizationId: report.organizationId || undefined,
        },
      }).unwrap();
      toast.success("Report updated successfully!");
      setIsEditing(false);
    } catch (error: any) {
      Sentry.captureException(error);
      console.error("UpdateReportError", error);
      toast.error("Failed to update report");
    }
  };

  const handleDownloadPDF = async () => {
    if (!report) return;

    try {
      // Fetch report data for PDF
      const pdfData = await getReportPDF({ id: id! }).unwrap();

      // Generate PDF using jsPDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = margin;
      const lineSpacing = 6;
      const sectionSpacing = 10;

      // Helper function to check if we need a new page
      const checkNewPage = (requiredSpace: number) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper function to add text with word wrap and pagination
      const addText = (
        text: string,
        fontSize: number,
        isBold = false,
        spacing = lineSpacing
      ) => {
        doc.setFontSize(fontSize);
        doc.setFont("helvetica", isBold ? "bold" : "normal");
        const lines = doc.splitTextToSize(text, maxWidth);
        
        lines.forEach((line: string) => {
          checkNewPage(fontSize * 0.4 + spacing);
          doc.text(line, margin, yPosition);
          yPosition += fontSize * 0.4 + spacing;
        });
      };

      // Helper function to add section heading
      const addSectionHeading = (text: string) => {
        checkNewPage(20);
        yPosition += 5;
        addText(text, 14, true, 8);
        yPosition += 3;
      };

      // Parse narrative into sections
      const parseNarrative = (narrative: string) => {
        const sections: { title: string; content: string }[] = [];
        
        // Section patterns - handle both numbered (1., 2., etc.) and unnumbered formats
        const sectionPatterns = [
          { pattern: /(?:^|\n)\s*(?:\d+\.\s*)?(?:Transparency\s*&\s*Ethical\s*Use\s*of\s*AI|Transparency|Ethical\s*Use\s*of\s*AI)[:\s]*\n/i, title: "Transparency & Ethical Use of AI" },
          { pattern: /(?:^|\n)\s*(?:\d+\.\s*)?About\s*the\s*Organization[:\s]*\n/i, title: "About the Organization" },
          { pattern: /(?:^|\n)\s*(?:\d+\.\s*)?Organization\s*Overview[:\s]*\n/i, title: "Organization Overview" },
          { pattern: /(?:^|\n)\s*(?:\d+\.\s*)?Community\s*Snapshot[:\s]*\n/i, title: "Community Snapshot" },
          { pattern: /(?:^|\n)\s*(?:\d+\.\s*)?Program\s*Highlights(?:\s*&\s*Outcomes)?[:\s]*\n/i, title: "Program Highlights" },
          { pattern: /(?:^|\n)\s*(?:\d+\.\s*)?Capacity\s*&\s*Sustainability[:\s]*\n/i, title: "Capacity & Sustainability" },
          { pattern: /(?:^|\n)\s*(?:\d+\.\s*)?Community\s*Voice\s*&\s*Public\s*Narrative[:\s]*\n/i, title: "Community Voice & Public Narrative" },
          { pattern: /(?:^|\n)\s*(?:\d+\.\s*)?Social\s*Justice\s*&\s*Accountability[:\s]*\n/i, title: "Social Justice & Accountability" },
          { pattern: /(?:^|\n)\s*(?:\d+\.\s*)?Closing\s*Reflection[:\s]*\n/i, title: "Closing Reflection" },
          { pattern: /(?:^|\n)\s*(?:\d+\.\s*)?AI\s*Transparency\s*Statement[:\s]*\n/i, title: "AI Transparency Statement" },
        ];

        // Remove title if present at the start
        let cleanNarrative = narrative.trim();
        if (cleanNarrative.startsWith("Ignite Community Impact Report")) {
          cleanNarrative = cleanNarrative.replace(/^Ignite\s+Community\s+Impact\s+Report\s*/i, "").trim();
        }

        // Remove numbered header sections (Organization:, Funder:, Reporting Period:)
        cleanNarrative = cleanNarrative.replace(
          /^(?:\d+\.\s*)?(?:Organization|Funder|Reporting\s+Period)[:\s].*?\n(?=\d+\.\s*(?:Transparency|About|Community|Program|Capacity|Closing|AI))/i,
          ""
        );

        // Find all section matches
        const matches: { index: number; title: string; pattern: RegExp }[] = [];
        sectionPatterns.forEach(({ pattern, title }) => {
          const match = cleanNarrative.match(pattern);
          if (match && match.index !== undefined) {
            matches.push({ index: match.index, title, pattern });
          }
        });

        // Sort matches by index
        matches.sort((a, b) => a.index - b.index);

        // Extract sections
        if (matches.length === 0) {
          // No sections found, treat entire narrative as one section
          sections.push({ title: "Report", content: cleanNarrative });
        } else {
          // Add content before first section (if any)
          if (matches[0].index > 0) {
            const intro = cleanNarrative.substring(0, matches[0].index).trim();
            // Remove any numbered header info
            const cleanIntro = intro.replace(
              /^(?:\d+\.\s*)?(?:Organization|Funder|Reporting\s+Period)[:\s].*$/gmi,
              ""
            ).trim();
            if (cleanIntro && !cleanIntro.match(/^(?:\d+\.\s*)?(?:Organization|Funder|Reporting\s+Period)/i)) {
              sections.push({ title: "Introduction", content: cleanIntro });
            }
          }

          // Add each section
          matches.forEach((match, idx) => {
            const startIndex = match.index;
            const endIndex =
              idx < matches.length - 1 ? matches[idx + 1].index : cleanNarrative.length;
            let content = cleanNarrative.substring(startIndex, endIndex).trim();
            
            // Remove the section title (with or without number) from content
            content = content.replace(
              new RegExp(`^(?:\\d+\\.\\s*)?${match.title.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}[:\\s]*`, "i"),
              ""
            ).trim();
            
            if (content) {
              sections.push({ title: match.title, content });
            }
          });
        }

        return sections;
      };

      // Title
      addText("Ignite Community Impact Report", 18, true, 10);
      yPosition += 5;

      // Organization info
      if (pdfData.report.organization) {
        addText(`Organization: ${pdfData.report.organization.name}`, 12, false, 4);
      }
      addText(`Funder: ${pdfData.report.grantName}`, 12, false, 4);
      const startDate = new Date(pdfData.report.startDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const endDate = new Date(pdfData.report.endDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      addText(`Reporting Period: ${startDate} - ${endDate}`, 12, false, 8);
      yPosition += 5;

      // Impact Metrics section
      if (
        pdfData.report.metrics &&
        pdfData.report.metrics.length > 0
      ) {
        addSectionHeading("Impact Metrics");
        addText("Each value below is the total for the reporting period.", 10, false, 4);
        yPosition += 2;
        pdfData.report.metrics.forEach((metric: any) => {
          const label = getMetricTotalLabel(metric.name || "");
          addText(`• ${label}: ${metric.value}`, 11, false, 4);
        });
        yPosition += 5;
      }

      // Parse and add narrative sections
      const narrative = pdfData.report.narrative || "No narrative provided";
      const sections = parseNarrative(narrative);

      sections.forEach((section) => {
        addSectionHeading(section.title);
        addText(section.content, 11, false, 5);
        yPosition += sectionSpacing;
      });

      // Save PDF
      const fileName = `Ignite_Report_${pdfData.report.grantName.replace(/\s+/g, "_")}.pdf`;
      doc.save(fileName);
      toast.success("PDF downloaded successfully!");
    } catch (error: any) {
      Sentry.captureException(error);
      console.error("PDFGenerationError", error);
      toast.error("Failed to generate PDF");
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm("Are you sure you want to delete this report?"))
      return;

    try {
      await deleteReport({ id }).unwrap();
      toast.success("Report deleted successfully");
      navigate("/");
    } catch (error: any) {
      Sentry.captureException(error);
      console.error("DeleteReportError", error);
      toast.error("Failed to delete report");
    }
  };

  if (isLoading) {
    return <FullPageNexusLogoAnimation />;
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-4">Report Not Found</h1>
          <Link to="/">
            <Button>Go to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const startDate = new Date(report.startDate).toLocaleDateString();
  const endDate = new Date(report.endDate).toLocaleDateString();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/")}
                className="flex-shrink-0"
              >
                ← Back
              </Button>
              <div>
                <h1 className="text-3xl font-semibold mb-2">{report.grantName}</h1>
                <p className="text-gray-600">
                  {startDate} - {endDate}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {!isEditing && (
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(true)}
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  Edit Report
                </Button>
              )}
              <Button
                onClick={handleDownloadPDF}
                className="bg-orange-500 hover:bg-orange-600"
                disabled={isEditing}
              >
                Download PDF
              </Button>
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={isDeleting || isEditing}
                className="text-red-600 hover:text-red-700"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>

          {/* Organization */}
          {report.organization && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Organization</p>
              <p className="font-semibold">{report.organization.name}</p>
            </div>
          )}

          {/* Impact Metrics */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-1">Impact Metrics</h2>
            <p className="text-sm text-gray-600 mb-4">Each value below is the total for the reporting period.</p>
            {report.metrics && report.metrics.length > 0 ? (
              <div className="space-y-2">
                {report.metrics.map((metric: any, index: number) => {
                  const label = getMetricTotalLabel(metric.name || "");
                  return (
                    <div
                      key={index}
                      className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                    >
                      <span className="font-medium">{label}</span>
                      <span className="text-gray-700">{metric.value}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500">No metrics provided</p>
            )}
          </div>

          {/* Narrative */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Narrative</h2>
              {isEditing && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      setEditedNarrative(report.narrative || "");
                    }}
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    disabled={isUpdating}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {isUpdating ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              )}
            </div>
            {isEditing ? (
              <div>
                <Label htmlFor="narrative" className="sr-only">
                  Narrative
                </Label>
                <Textarea
                  id="narrative"
                  value={editedNarrative}
                  onChange={(e) => setEditedNarrative(e.target.value)}
                  className="min-h-[400px] font-sans"
                  placeholder="Enter report narrative..."
                />
              </div>
            ) : (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="whitespace-pre-wrap">
                  {report.narrative || "No narrative provided"}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="pt-4 border-t flex justify-end">
            <Link to="/">
              <Button variant="outline">Back to Dashboard</Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewReport;

